import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/agendar.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function AgendarTurno({ token }) {
  const [paciente, setPaciente] = useState(null);
  const [turnos, setTurnos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [vistaActual, setVistaActual] = useState('mis-turnos'); // 'mis-turnos' o 'agendar'
  
  // Estados para agendar nuevo turno
  const [fechaSeleccionada, setFechaSeleccionada] = useState('');
  const [horariosDisponibles, setHorariosDisponibles] = useState([]);
  const [horaSeleccionada, setHoraSeleccionada] = useState('');
  const [notasTurno, setNotasTurno] = useState('');
  const [loadingHorarios, setLoadingHorarios] = useState(false);
  const [agendando, setAgendando] = useState(false);

  useEffect(() => {
    validarTokenYCargar();
  }, [token]);

  const validarTokenYCargar = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Validar token y obtener datos del paciente
      const pacienteRes = await axios.get(`${API_URL}/public/paciente/${token}`);
      setPaciente(pacienteRes.data.paciente);
      
      // Cargar turnos del paciente
      const turnosRes = await axios.get(`${API_URL}/public/paciente/${token}/turnos`);
      setTurnos(turnosRes.data.turnos || []);
      
    } catch (err) {
      console.error('Error:', err);
      setError('Link inválido o expirado. Por favor contacta al consultorio.');
    } finally {
      setLoading(false);
    }
  };

  const cargarHorariosDisponibles = async (fecha) => {
    try {
      setLoadingHorarios(true);
      const res = await axios.get(`${API_URL}/public/horarios-disponibles?fecha=${fecha}`);
      setHorariosDisponibles(res.data.horarios || []);
    } catch (err) {
      console.error('Error al cargar horarios:', err);
      alert('Error al cargar horarios disponibles');
    } finally {
      setLoadingHorarios(false);
    }
  };

  const handleFechaChange = (e) => {
    const fecha = e.target.value;
    setFechaSeleccionada(fecha);
    setHoraSeleccionada('');
    if (fecha) {
      cargarHorariosDisponibles(fecha);
    } else {
      setHorariosDisponibles([]);
    }
  };

  const handleAgendarTurno = async (e) => {
    e.preventDefault();
    
    if (!fechaSeleccionada || !horaSeleccionada) {
      alert('Por favor selecciona fecha y hora');
      return;
    }
    
    try {
      setAgendando(true);
      await axios.post(`${API_URL}/public/paciente/${token}/agendar`, {
        fecha: fechaSeleccionada,
        hora: horaSeleccionada,
        notas: notasTurno
      });
      
      alert('✅ Turno agendado exitosamente');
      
      // Resetear formulario y volver a mis turnos
      setFechaSeleccionada('');
      setHoraSeleccionada('');
      setNotasTurno('');
      setHorariosDisponibles([]);
      setVistaActual('mis-turnos');
      
      // Recargar turnos
      validarTokenYCargar();
      
    } catch (err) {
      console.error('Error:', err);
      alert(err.response?.data?.error || 'Error al agendar turno');
    } finally {
      setAgendando(false);
    }
  };

  const handleCancelarTurno = async (turnoId) => {
    if (!window.confirm('¿Estás seguro de cancelar este turno?')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/public/paciente/${token}/turnos/${turnoId}`);
      alert('Turno cancelado exitosamente');
      validarTokenYCargar();
    } catch (err) {
      console.error('Error:', err);
      alert(err.response?.data?.error || 'Error al cancelar turno');
    }
  };

  const formatFecha = (fechaHora) => {
    const fecha = new Date(fechaHora);
    return fecha.toLocaleDateString('es-AR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const obtenerFechaMinima = () => {
    const hoy = new Date();
    hoy.setDate(hoy.getDate() + 1); // Mínimo mañana
    return hoy.toISOString().split('T')[0];
  };

  const obtenerFechaMaxima = () => {
    const hoy = new Date();
    hoy.setDate(hoy.getDate() + 60); // Máximo 60 días
    return hoy.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="agendar-container">
        <div className="loading">Cargando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="agendar-container">
        <div className="error-mensaje">
          <h2>❌ Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="agendar-container">
      <header className="agendar-header">
        <h1>🏥 Portal del Paciente</h1>
        <p className="paciente-nombre">Hola, {paciente?.nombre}</p>
      </header>

      <nav className="agendar-nav">
        <button 
          className={vistaActual === 'mis-turnos' ? 'active' : ''}
          onClick={() => setVistaActual('mis-turnos')}
        >
          📋 Mis Turnos
        </button>
        <button 
          className={vistaActual === 'agendar' ? 'active' : ''}
          onClick={() => setVistaActual('agendar')}
        >
          ➕ Agendar Turno
        </button>
      </nav>

      {vistaActual === 'mis-turnos' && (
        <div className="mis-turnos-seccion">
          <h2>Mis Turnos</h2>
          
          {turnos.length === 0 ? (
            <div className="sin-datos">
              <p>No tienes turnos agendados</p>
              <button 
                className="btn-primario"
                onClick={() => setVistaActual('agendar')}
              >
                Agendar mi primer turno
              </button>
            </div>
          ) : (
            <div className="turnos-lista">
              {turnos.map(turno => (
                <div key={turno.id} className={`turno-card ${turno.estado}`}>
                  <div className="turno-header">
                    <h3>{formatFecha(turno.fecha_hora)}</h3>
                    <span className={`estado-badge ${turno.estado}`}>
                      {turno.estado.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="turno-detalles">
                    {turno.psicologo_nombre && (
                      <p><strong>Profesional:</strong> {turno.psicologo_nombre}</p>
                    )}
                    <p><strong>Duración:</strong> {turno.duracion} minutos</p>
                    {turno.notas && (
                      <p><strong>Notas:</strong> {turno.notas}</p>
                    )}
                  </div>
                  
                  {turno.estado === 'pendiente' && (
                    <button 
                      className="btn-cancelar-turno"
                      onClick={() => handleCancelarTurno(turno.id)}
                    >
                      Cancelar Turno
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {vistaActual === 'agendar' && (
        <div className="agendar-seccion">
          <h2>Agendar Nuevo Turno</h2>
          
          <form onSubmit={handleAgendarTurno} className="agendar-form">
            <div className="form-group">
              <label>Selecciona una fecha</label>
              <input 
                type="date"
                value={fechaSeleccionada}
                onChange={handleFechaChange}
                min={obtenerFechaMinima()}
                max={obtenerFechaMaxima()}
                required
              />
              <small>Puedes agendar turnos con al menos 1 día de anticipación</small>
            </div>

            {fechaSeleccionada && (
              <div className="form-group">
                <label>Horarios disponibles</label>
                {loadingHorarios ? (
                  <p className="loading-horarios">Cargando horarios...</p>
                ) : horariosDisponibles.length === 0 ? (
                  <p className="sin-horarios">No hay horarios disponibles para esta fecha</p>
                ) : (
                  <div className="horarios-grid">
                    {horariosDisponibles.map(hora => (
                      <button
                        key={hora}
                        type="button"
                        className={`horario-btn ${horaSeleccionada === hora ? 'selected' : ''}`}
                        onClick={() => setHoraSeleccionada(hora)}
                      >
                        {hora}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {horaSeleccionada && (
              <>
                <div className="form-group">
                  <label>Notas o motivo (opcional)</label>
                  <textarea 
                    value={notasTurno}
                    onChange={(e) => setNotasTurno(e.target.value)}
                    placeholder="Escribe aquí el motivo de la consulta o cualquier información relevante..."
                    rows="4"
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn-agendar"
                  disabled={agendando}
                >
                  {agendando ? 'Agendando...' : '✅ Confirmar Turno'}
                </button>
              </>
            )}
          </form>
        </div>
      )}

      <footer className="agendar-footer">
        <p>Para consultas, comunícate con el consultorio</p>
        <p className="contacto-info">
          📧 {paciente?.email} | 📱 {paciente?.telefono}
        </p>
      </footer>
    </div>
  );
}

export default AgendarTurno;
